import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { Button, Dropdown, Layout, theme, Typography } from "antd"
import Table, { ColumnsType } from "antd/es/table"
import { UserOutlined } from "@ant-design/icons"
import classNames from "classnames"

import { affiliateProgramStore } from "@src/app/affiliateProgramStore"
import merchantIcon from "@src/app/assets/affiliate/merchant.svg"
import ref from "@src/app/assets/affiliate/ref.svg"
import usdt from "@src/app/assets/affiliate/usdt.svg"
// @ts-ignore
import referralTerms from "@src/app/assets/pdf/terms_and_conditions_cryptix_referral.pdf"
import { authStore } from "@src/app/authStore"
import { AffiliateBlock } from "@src/app/components/AffiliateBlock"
import { AffiliateLink } from "@src/app/components/AffiliateLink"
import { Logo } from "@src/app/components/Logo"
import { ModalInfo } from "@src/app/components/ModalInfo"
import { OptionallyVisible } from "@src/app/components/OptionallyVisible"
import { paginationConfig } from "@src/app/configs"
import { paths } from "@src/app/paths"
import { catchErrors } from "@src/shared/api"
import { dateTimeFormat } from "@src/shared/date"
import { useBreakpoint } from "@src/shared/hooks/useBreakpoints"
import css from "./index.module.css"

const { Title } = Typography

const { Footer, Header } = Layout

export const AffiliateProgram = observer(() => {
  const [isStateCopy, setIsStateCopy] = useState(true)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const { isMobile: isMobBreakpoint } = useBreakpoint()

  const { token } = theme.useToken()

  const navigate = useNavigate()

  const { logout, merchant } = authStore

  const {
    available,
    deleteLinks,
    generateLink,
    getLinks,
    isLoading,
    links,
    pageSize,
    referrals,
    total,
  } = affiliateProgramStore

  const columns: ColumnsType<any> = [
    {
      dataIndex: "id",
      title: "ID",
      width: "20%",
    },
    {
      dataIndex: "created_at",
      render: (_, { created_at }) => dateTimeFormat(created_at as string),
      title: "Date and Time",
      width: "20%",
    },
    {
      dataIndex: "is_active",
      render: (_, { is_active }) => <span>{is_active ? "Yes" : "No"}</span>,
      title: "Status of activation",
      width: "20%",
    },
    {
      dataIndex: "email",
      title: "Email",
      width: "20%",
    },
    {
      dataIndex: "total_transactions",
      title: "Amount of transactions",
      width: "20%",
    },
  ]

  const handleGenerateLink = () => {
    generateLink().catch(catchErrors)
  }

  const handleDeleteLinks = () => {
    deleteLinks()
      .then(() => getLinks())
      .catch(catchErrors)
      .finally(() => setIsStateCopy(true))
  }

  const open = () => {
    const isMobile = /Android|iPhone|iPad|iPod|Windows Phone/i.test(
      navigator.userAgent,
    )

    if (!(isMobile || isMobBreakpoint)) {
      return setIsModalOpen(true)
    }

    setTimeout(() => {
      try {
        const newWindow = window.open(referralTerms, "_blank")

        if (!newWindow) {
          console.warn("[PDF] window.open заблокирован. Пробуем скачать.")
        }
      } catch (downloadErr) {
        console.error(
          "[PDF] Ошибка во время открытия или скачивания:",
          downloadErr,
        )
      }
    }, 0)
  }

  return (
    <div>
      <Header
        style={{
          alignItems: "center",
          background: token.colorBgLayout,
          display: "flex",
          justifyContent: "space-between",
          textAlign: "right",
        }}
      >
        <Logo size="full" />
        <div>
          <Dropdown
            menu={{
              items: [
                {
                  key: "exit",
                  label: (
                    <a
                      onClick={() => logout().then(() => navigate(paths.auth))}
                    >
                      Exit
                    </a>
                  ),
                },
              ],
            }}
          >
            <Button
              icon={<UserOutlined />}
              id="GENERATED_ID_AFFILIATEPROGRAM_BUTTON_VOID_1"
              size="large"
            >
              {merchant?.email}
            </Button>
          </Dropdown>
        </div>
      </Header>
      <div className={css.container}>
        <div className={css.wrapper}>
          <AffiliateBlock
            amount="0"
            currency="USDT"
            icon={usdt}
            title="Current Balance"
          >
            <Button
              disabled
              id="GENERATED_ID_AFFILIATEPROGRAM_BUTTON_REQUEST_MY_REWARD_1"
              size="large"
              type="primary"
            >
              Request my reward
            </Button>
          </AffiliateBlock>
          <AffiliateBlock amount={total} icon={ref} title="My Referrals" />
          <AffiliateBlock
            amount={available}
            icon={merchantIcon}
            title="Active Merchants"
          />
        </div>
        <div className={css.wrapper}>
          <div className={css.content}>
            <Title className={css.title} level={4}>
              Refer and earn commissions
            </Title>
            <ol className={css.list}>
              <li>
                Refer a merchant. He needs to sign up using your unique referral
                link.
              </li>
              <li>
                The merchant will pass our KYB check, activate the account, and
                start conducting transactions.
              </li>
              <li>
                Earn 15% for 6 months from the date the merchant's account is
                activated with each commission taken by us from the merchant.
                Full terms and conditions are available{" "}
                <Button
                  id="GENERATED_ID_AFFILIATEPROGRAM_BUTTON_VIA_THE_LINK_1"
                  onClick={() => open()}
                  style={{ padding: 0 }}
                  type="link"
                >
                  via the link
                </Button>
                .
              </li>
            </ol>
          </div>
          <div className={css.content}>
            <div className={css.linksHeader}>
              <Title className={css.title} level={4}>
                My referral link
              </Title>
              <OptionallyVisible visible={isStateCopy}>
                <div className={css.buttonsWrapper}>
                  {Boolean(links.length) && (
                    <Button
                      className={css.deleteButton}
                      id="GENERATED_ID_AFFILIATEPROGRAM_BUTTON_DELETE_LINK_1"
                      onClick={() => setIsStateCopy(false)}
                      size="large"
                      type="text"
                    >
                      Delete link
                    </Button>
                  )}
                  <Button
                    className={css.button}
                    id="GENERATED_ID_AFFILIATEPROGRAM_BUTTON_GENERATE_LINK_1"
                    onClick={handleGenerateLink}
                    size="large"
                    type="primary"
                  >
                    Generate link
                  </Button>
                </div>
              </OptionallyVisible>
              <OptionallyVisible visible={!isStateCopy}>
                <Button
                  className={classNames(css.button, css.saveButton)}
                  id="GENERATED_ID_AFFILIATEPROGRAM_BUTTON_SAVE_1"
                  loading={isLoading}
                  onClick={handleDeleteLinks}
                  size="large"
                  type="primary"
                >
                  Save
                </Button>
              </OptionallyVisible>
            </div>
            <div className={css.linksText}>
              you can generate up to five personal referral links
            </div>
            <div className={css.linksWrapper}>
              {links.map((link) => (
                <AffiliateLink
                  id={link.id}
                  key={link.id}
                  link={link.url}
                  message={isStateCopy ? "LInk copied" : ""}
                  state={isStateCopy ? "copy" : "delete"}
                />
              ))}
            </div>
          </div>
        </div>
        <div className={css.tableWrapper}>
          <div className={css.tableHeader}>
            <Title className={css.title} level={4}>
              My Referrals
            </Title>
          </div>
          <Table
            columns={columns}
            // @ts-ignore
            dataSource={referrals}
            loading={false}
            pagination={{
              pageSize,
              total,
              ...paginationConfig,
            }}
            style={{ width: "100%" }}
          />
        </div>
      </div>
      <Footer className={css.footer}>
        <span>{`© ${new Date().getFullYear()} Cryptix`}</span>
        <div>
          <a href="https://www.cryptix.io/about-us" target={"_blank"}>
            About
          </a>
          <a href="https://www.cryptix.io/service-fees" target={"_blank"}>
            Service fees
          </a>
          <a href="mailto:support@cryptix.io">Support</a>
          <a href="https://t.me/cryptix_chat" target={"_blank"}>
            Contact Us
          </a>
          <a href="https://developers.cryptix.io/" target={"_blank"}>
            API Documentation
          </a>
        </div>
      </Footer>
      <ModalInfo
        contentKey="referrer"
        onClose={() => setIsModalOpen(false)}
        open={isModalOpen}
      />
    </div>
  )
})
